import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Grid,
} from '@mui/material';
import { Close, Settings, Add, Delete, Edit, ExpandMore } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { formatAppDateTime } from '../../services/appConfigRuntime';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-flow-properties`;
const PARAM_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/flow-parameters`;
const CONTAINERS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync-containers`;
const CONTAINER_ASSIGNMENTS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync-container-assignments`;
const ASSIGNED_CONTAINERS_API_URL = `${CONTAINER_ASSIGNMENTS_API_URL}/sync-file`;
const RUN_STATUS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/sync/runs`;
const DATAHUB_API_LOG_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/api/datahub/api-logs`;
const MASTER_CONNECTOR_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-connector`;
const CONNECTOR_ASSIGNMENT_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/flow-connector-assignment`;

// UUID generator function
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const token = localStorage.getItem('token');

/** Hub params allowed with empty value until first run fills them (e.g. incremental watermark). */
const hubParamsAllowEmptyValue = (name) =>
  ['RecordsToProcess', 'RunMode', 'LastModifiedAfter'].includes(name);

/** True when param belongs to source, target, or is flow-level (no connector). */
const paramMatchesHubConnectors = (paramMcId, allowedMcIds) => {
  if (paramMcId == null || paramMcId === '') {
    return true; // flow-level / unscoped master params
  }
  if (!allowedMcIds || allowedMcIds.size === 0) {
    return false;
  }
  return allowedMcIds.has(paramMcId.toString());
};

const HubFlowParameter = ({
  open,
  onClose,
  masterFlowId,
  flowInstanceId,
  masterFlowName,
  sfiSourceConnectorId,
  sfiTargetConnectorId,
  isHubFlow = false,
  uiConfig,
}) => {
  const shouldShowSection = (sectionKey) => {
    if (uiConfig?.sections && typeof uiConfig.sections[sectionKey] === 'boolean') {
      return uiConfig.sections[sectionKey];
    }
    return true;
  };
  const [parameters, setParameters] = useState([]);
  const [runStatusRecords, setRunStatusRecords] = useState([]);
  const [apiLogRecords, setApiLogRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentParameter, setCurrentParameter] = useState(null);
  const [paramValue, setParamValue] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [availableParameters, setAvailableParameters] = useState([]);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState('');
  const [containers, setContainers] = useState([]);
  const [selectedContainer, setSelectedContainer] = useState('');
  const [assignContainerModalOpen, setAssignContainerModalOpen] = useState(false);
  const [assignedContainers, setAssignedContainers] = useState([]);
  const [selectedContainers, setSelectedContainers] = useState([]);
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState('parameters');
  const [masterConnectors, setMasterConnectors] = useState([]);
  const [assignedConnectorsData, setAssignedConnectorsData] = useState([]);
  const [availableConnectorsForAssignment, setAvailableConnectorsForAssignment] = useState([]);
  const [connectorModalOpen, setConnectorModalOpen] = useState(false);
  const [selectedConnectorType, setSelectedConnectorType] = useState('');
  const [selectedConnectorId, setSelectedConnectorId] = useState('');
  const [loadingAssignedConnectors, setLoadingAssignedConnectors] = useState(false);
  const [hubConnectorMcIds, setHubConnectorMcIds] = useState([]);
  const [detailsPopupOpen, setDetailsPopupOpen] = useState(false);
  const [selectedRunDetails, setSelectedRunDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const hubMcIdSet = useMemo(
    () => new Set((hubConnectorMcIds || []).map((id) => id.toString())),
    [hubConnectorMcIds]
  );

  const filteredParameters = useMemo(() => {
    if (!isHubFlow) return parameters;
    if (hubMcIdSet.size === 0) return parameters;
    return parameters.filter((param) => paramMatchesHubConnectors(param.sfpMcId, hubMcIdSet));
  }, [parameters, hubMcIdSet, isHubFlow]);

  const filteredAvailableParameters = useMemo(() => {
    if (!isHubFlow) return availableParameters;
    if (hubMcIdSet.size === 0) return availableParameters;
    return availableParameters.filter((param) => paramMatchesHubConnectors(param.mfpMcId, hubMcIdSet));
  }, [availableParameters, hubMcIdSet, isHubFlow]);

  const sortedRunStatusRecords = useMemo(() => {
    return [...runStatusRecords].sort((a, b) => {
      const timeA = new Date(a.srsStartDateTime || a.srsEndDateTime || 0).getTime();
      const timeB = new Date(b.srsStartDateTime || b.srsEndDateTime || 0).getTime();
      return timeB - timeA;
    });
  }, [runStatusRecords]);

  useEffect(() => {
    if (open && (flowInstanceId || masterFlowId)) {
      fetchMasterConnectors(); // Fetch first to determine which connector types to show
      fetchParameters();
      fetchContainers();
      fetchAssignedContainers();
      fetchAssignedConnectors();
      fetchRunStatusRecords();
      fetchApiLogRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flowInstanceId, masterFlowId, sfiSourceConnectorId, sfiTargetConnectorId, isHubFlow]);

  useEffect(() => {
    // Reset parameter edit state when drawer closes or flow changes
    setEditMode(false);
    setCurrentParameter(null);
    setParamValue('');
  }, [open, flowInstanceId, masterFlowId]);

  const fetchMasterConnectors = async () => {
    try {
      if (isHubFlow && flowInstanceId) {
        // For Hub Flow: resolve connector types from selected source/target.
        // Supports both new master IDs and legacy sync connector IDs.
        const hubFlowConnectors = [];
        const allConnectorsResponse = await fetch(MASTER_CONNECTOR_API_URL, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const allConnectors = allConnectorsResponse.ok ? await allConnectorsResponse.json() : [];
        const connectorsArray = Array.isArray(allConnectors) ? allConnectors : [];

        const resolveMasterConnector = async (selectedId) => {
          if (!selectedId) return null;
          const selectedIdStr = selectedId.toString();

          // New behavior: HubFlow stores master connector IDs directly.
          const masterDirect = connectorsArray.find((mc) => mc.mcId?.toString() === selectedIdStr);
          if (masterDirect) {
            return masterDirect;
          }

          // Backward compatibility: older rows may still store sync connector IDs.
          try {
            const syncConnectorResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors/${selectedId}`,
              {
                method: 'GET',
                headers: {
                  Accept: 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            if (!syncConnectorResponse.ok) return null;
            const syncConnector = await syncConnectorResponse.json();
            return connectorsArray.find((mc) => mc.mcId?.toString() === syncConnector?.mcId?.toString()) || null;
          } catch (error) {
            console.error('Error resolving master connector from selected ID:', error);
            return null;
          }
        };

        let resolvedSourceMcId = null;
        let resolvedTargetMcId = null;
        if (sfiSourceConnectorId) {
          const sourceMasterConnector = await resolveMasterConnector(sfiSourceConnectorId);
          if (sourceMasterConnector) {
            hubFlowConnectors.push(sourceMasterConnector);
            resolvedSourceMcId = sourceMasterConnector.mcId;
            console.log('Hub Flow source connector type:', sourceMasterConnector.mcName);
          }
        }

        if (sfiTargetConnectorId) {
          const targetMasterConnector = await resolveMasterConnector(sfiTargetConnectorId);
          if (targetMasterConnector) {
            const exists = hubFlowConnectors.some((c) => c.mcId?.toString() === targetMasterConnector.mcId?.toString());
            if (!exists) {
              hubFlowConnectors.push(targetMasterConnector);
            }
            resolvedTargetMcId = targetMasterConnector.mcId;
            console.log('Hub Flow target connector type:', targetMasterConnector.mcName);
          }
        }

        const mcIds = [resolvedSourceMcId, resolvedTargetMcId].filter((id) => id != null && id !== '');
        setHubConnectorMcIds(mcIds);

        console.log(`Hub Flow uses ${hubFlowConnectors.length} connector types:`, hubFlowConnectors.map(c => c.mcName));
        setMasterConnectors(hubFlowConnectors);
      } else {
        setHubConnectorMcIds([]);
        // For Sync Flow: Get connector types from master flow (existing logic)
        console.log('Fetching connectors for master flow ID:', masterFlowId);

        if (!masterFlowId) {
          console.warn('No master flow ID provided, showing all connectors');
          const allConnectorsResponse = await fetch(MASTER_CONNECTOR_API_URL);
          const allConnectors = await allConnectorsResponse.json();
          setMasterConnectors(Array.isArray(allConnectors) ? allConnectors : []);
          return;
        }

        const connectorsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/master-flow/${masterFlowId}/connectors`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!connectorsResponse.ok) {
          console.warn(`Could not fetch flow connectors (status: ${connectorsResponse.status}), showing all connectors`);
          const allConnectorsResponse = await fetch(MASTER_CONNECTOR_API_URL);
          const allConnectors = await allConnectorsResponse.json();
          setMasterConnectors(Array.isArray(allConnectors) ? allConnectors : []);
          return;
        }

        const connectorIds = await connectorsResponse.json();
        console.log('Connector IDs used by this flow:', connectorIds);

        const allConnectorsResponse = await fetch(MASTER_CONNECTOR_API_URL);
        const allConnectors = await allConnectorsResponse.json();
        const connectorsArray = Array.isArray(allConnectors) ? allConnectors : [];
        const flowConnectors = connectorsArray.filter(connector =>
          connectorIds.includes(connector.mcId)
        );

        console.log(`Flow uses ${flowConnectors.length} connector types:`, flowConnectors.map(c => c.mcName));
        setMasterConnectors(flowConnectors);
      }
    } catch (error) {
      console.error('Error fetching master connectors:', error);
      toast.error(`Failed to load master connectors: ${error.message}`);
    }
  };

  const fetchParameters = async () => {
    if (!masterFlowId) return;

    setLoading(true);
    try {
      // For Hub Flows: Pass both masterFlowId and flowInstanceId
      // For Sync Flows: Pass only masterFlowId (instanceId will be null)
      const url = flowInstanceId
        ? `${API_BASE_URL}/${masterFlowId}?instanceId=${flowInstanceId}`
        : `${API_BASE_URL}/${masterFlowId}`;

      const response = await fetch(url,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setParameters(data);
    } catch (error) {
      console.error('Error fetching parameters:', error);
      toast.error(`Failed to load parameters: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchRunStatusRecords = async () => {
    // For Hub Flows: query by both flowId (master flow ID) and instanceId (instance ID) for precision
    // For Sync Flows: query by flowId (master flow ID) only
    let url = '';
    if (isHubFlow && flowInstanceId && masterFlowId) {
      // Hub Flow: query by both master flow ID (flowId) and instance ID (instanceId)
      // This ensures we only get run status for this specific Hub Flow instance
      url = `${RUN_STATUS_API_URL}?flowId=${masterFlowId}&instanceId=${flowInstanceId}`;
    } else if (masterFlowId) {
      // Sync Flow: query by master flow ID only (instanceId will be null in database)
      url = `${RUN_STATUS_API_URL}?flowId=${masterFlowId}`;
    } else {
      return; // No valid ID to query
    }

    setLoading(true);
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setRunStatusRecords(data);
    } catch (error) {
      console.error('Error fetching run status records:', error);
      toast.error(`Failed to load run status records: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiLogRecords = async () => {
    // For Hub Flows, use instance ID to fetch API logs
    if (!isHubFlow || !flowInstanceId) return;
    setLoading(true);
    try {
      const response = await fetch(`${DATAHUB_API_LOG_API_URL}?flowId=${flowInstanceId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setApiLogRecords(data);
    } catch (error) {
      console.error('Error fetching API log records:', error);
      toast.error(`Failed to load API log records: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedContainers = async () => {
    if (!masterFlowId) return;

    setLoading(true);
    try {
      // For Hub Flows: Pass both masterFlowId and flowInstanceId
      // For Sync Flows: Pass only masterFlowId (instanceId will be null)
      const url = flowInstanceId
        ? `${ASSIGNED_CONTAINERS_API_URL}/${masterFlowId}?instanceId=${flowInstanceId}`
        : `${ASSIGNED_CONTAINERS_API_URL}/${masterFlowId}`;

      const response = await fetch(url,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setAssignedContainers(data);
      const assignedConIds = data.map((assignment) => assignment.conId);
      setSelectedContainers(assignedConIds);
    } catch (error) {
      console.error('Error fetching assigned containers:', error);
      toast.error(`Failed to load assigned containers: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchContainers = async () => {
    setLoadingContainers(true);
    try {
      const tenantId = localStorage.getItem('tenantId');
      // For Hub Flow: Filter by instanceId (flowInstanceId)
      // For Sync Flow: Get all containers (no instanceId filter)
      const url = isHubFlow && flowInstanceId
        ? `${CONTAINERS_API_URL}/tenant/${tenantId}?instanceId=${flowInstanceId}`
        : `${CONTAINERS_API_URL}/tenant/${tenantId}`;

      const response = await fetch(url,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setContainers(data);
    } catch (error) {
      console.error('Error fetching containers:', error);
      toast.error(`Failed to load containers: ${error.message}`);
    } finally {
      setLoadingContainers(false);
    }
  };

  const fetchAssignedConnectors = async () => {
    if (!masterFlowId) return;

    setLoadingAssignedConnectors(true);
    try {
      // For Hub Flows: Pass both masterFlowId and flowInstanceId
      // For Sync Flows: Pass only masterFlowId (instanceId will be null)
      const url = flowInstanceId
        ? `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}?instanceId=${flowInstanceId}`
        : `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setAssignedConnectorsData(data);
      console.log(`Fetched ${data.length} assigned connectors for flow: ${masterFlowId} (instanceId: ${flowInstanceId})`);
    } catch (error) {
      console.error('Error fetching assigned connectors:', error);
      toast.error(`Failed to load assigned connectors: ${error.message}`);
    } finally {
      setLoadingAssignedConnectors(false);
    }
  };

  const handleOpenConnectorModal = (connectorType) => {
    setSelectedConnectorType(connectorType);
    setSelectedConnectorId('');
    fetchAvailableConnectorsForType(connectorType);
    setConnectorModalOpen(true);
  };

  const fetchAvailableConnectorsForType = async (connectorType) => {
    setLoadingAssignedConnectors(true);
    try {
      const tenantId = localStorage.getItem('tenantId');
      const masterConnector = masterConnectors.find(mc => mc.mcName === connectorType);
      if (!masterConnector) {
        toast.error('Master connector not found');
        setLoadingAssignedConnectors(false);
        return;
      }

      const response = await fetch(
        `${CONNECTOR_ASSIGNMENT_API_URL}/available/${masterConnector.mcId}?tenantId=${tenantId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      const data = await response.json();
      // Ensure data is an array and has the expected structure
      if (Array.isArray(data)) {
        setAvailableConnectorsForAssignment(data);
      } else {
        console.error('Unexpected response format:', data);
        toast.error('Invalid response format from server');
        setAvailableConnectorsForAssignment([]);
      }
    } catch (error) {
      console.error('Error fetching available connectors:', error);
      toast.error(`Failed to load available connectors: ${error.message}`);
    } finally {
      setLoadingAssignedConnectors(false);
    }
  };

  const handleAssignConnector = async () => {
    if (!selectedConnectorId) {
      toast.error('Please select a connector');
      return;
    }

    if (!masterFlowId) {
      toast.error('Flow ID is missing');
      return;
    }

    try {
      const tenantId = localStorage.getItem('tenantId');
      const response = await fetch(CONNECTOR_ASSIGNMENT_API_URL, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          flowId: masterFlowId,
          instanceId: flowInstanceId || null, // Instance ID for Hub Flows, null for Sync Flows
          connectorType: selectedConnectorType,
          connectorId: selectedConnectorId,
          tenantId: parseInt(tenantId)
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      toast.success(`Connector assigned successfully`);
      setConnectorModalOpen(false);
      setSelectedConnectorId('');
      setSelectedConnectorType('');
      fetchAssignedConnectors();
    } catch (error) {
      console.error('Error assigning connector:', error);
      toast.error(`Failed to assign connector: ${error.message}`);
    }
  };

  const handleRemoveConnectorAssignment = async (connectorType) => {
    if (!masterFlowId) {
      toast.error('Flow ID is missing');
      return;
    }

    try {
      // For Hub Flows, include instanceId as query parameter
      const url = flowInstanceId
        ? `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}/type/${connectorType}?instanceId=${flowInstanceId}`
        : `${CONNECTOR_ASSIGNMENT_API_URL}/flow/${masterFlowId}/type/${connectorType}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      toast.success(`Connector assignment removed`);
      fetchAssignedConnectors();
    } catch (error) {
      console.error('Error removing connector assignment:', error);
      toast.error(`Failed to remove assignment: ${error.message}`);
    }
  };

  const fetchAvailableParameters = async () => {
    if (!masterFlowId) return;
    setLoadingParameters(true);
    try {
      const response = await fetch(`${PARAM_API_URL}/flow/${masterFlowId}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setAvailableParameters(data);
    } catch (error) {
      console.error('Error fetching available parameters:', error);
      toast.error(`Failed to load parameters: ${error.message}`);
    } finally {
      setLoadingParameters(false);
    }
  };

  const handleOpenAddModal = () => {
    setModalOpen(true);
    setSelectedParameter('');
    setParamValue('');
    fetchAvailableParameters();
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedParameter('');
    setParamValue('');
  };

  const handleCloseAssignContainerModal = () => {
    setAssignContainerModalOpen(false);
    setSelectedContainer('');
  };

  const handleParameterSelect = (e) => {
    const selectedParamId = e.target.value;
    setSelectedParameter(selectedParamId);
    const selectedParam = availableParameters.find((p) => p.mfpId === selectedParamId);
    if (selectedParam) {
      const parameterName = selectedParam.mfpName;
      const connectorMapping = {
        QuickbookConnectionId: 'Quickbook',
        AutodeskConnectionId: 'Autodesk',
        AutodeskDocsConnectionId: 'Autodesk Docs',
        SalesforceConnectionId: 'Salesforce',
        FusionConnectionId: 'FusionOperation',
        DynamicsConnectionId: 'Business centre',
      };

      if (parameterName in connectorMapping) {
        const connectorName = connectorMapping[parameterName];
        const connector = masterConnectors.find((mc) => mc.mcName === connectorName);
        if (connector) {
          setParamValue(connector.mcId);
        } else {
          setParamValue('');
        }
      } else {
        setParamValue('');
      }
    } else {
      setParamValue('');
    }
  };

  const handleContainerToggle = (conId) => {
    setSelectedContainers((prev) =>
      prev.includes(conId)
        ? prev.filter((id) => id !== conId)
        : [...prev, conId]
    );
  };

  const handleSaveContainers = async () => {
    if (!masterFlowId) {
      toast.warning('Master Flow ID is required');
      return;
    }
    setLoading(true);
    try {
      const unassignPromises = assignedContainers
        .filter((assignment) => !selectedContainers.includes(assignment.conId))
        .map(async (assignment) => {
          const response = await fetch(`${CONTAINER_ASSIGNMENTS_API_URL}/${assignment.scaId}`, {
            method: 'DELETE',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        });

      await Promise.all(unassignPromises);

      if (!masterFlowId) {
        toast.error('Flow ID is missing');
        return;
      }

      const assignPromises = selectedContainers
        .filter((conId) => !assignedContainers.some((assignment) => assignment.conId === conId))
        .map(async (conId) => {
          const assignmentData = {
            conId,
            sflId: masterFlowId,
            scaInstanceId: flowInstanceId || null, // Instance ID for Hub Flows, null for Sync Flows
          };
          const response = await fetch(CONTAINER_ASSIGNMENTS_API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(assignmentData),
          });
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return response.json();
        });

      const newAssignments = await Promise.all(assignPromises);
      const remainingAssignments = assignedContainers.filter((assignment) =>
        selectedContainers.includes(assignment.conId)
      );
      setAssignedContainers([...remainingAssignments, ...newAssignments]);
      toast.success('Containers updated successfully');
    } catch (error) {
      console.error('Error updating containers:', error);
      toast.error(`Failed to update containers: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddParameter = () => {
    if (!selectedParameter) {
      toast.warning('Please select a parameter');
      return;
    }

    const selectedParam = availableParameters.find((p) => p.mfpId === selectedParameter);
    if (!selectedParam) {
      toast.error('Selected parameter not found');
      return;
    }

    const existingParam = parameters.find((p) => p.sfpName === selectedParam.mfpName);
    if (existingParam) {
      toast.warning(`Parameter "${selectedParam.mfpName}" already exists. Please edit the existing parameter instead.`);
      return;
    }

    if (!hubParamsAllowEmptyValue(selectedParam.mfpName) && !paramValue.trim()) {
      toast.warning('Parameter value is required');
      return;
    }

    if (!masterFlowId) {
      toast.error('Flow ID is missing');
      return;
    }

    const newParam = {
      sfpName: selectedParam.mfpName,
      sfpValue: paramValue,
      sfpMfId: masterFlowId,
      sfpInstanceId: flowInstanceId || null, // Add instance ID for Hub Flows, null for Sync Flows
      sfpMcId: selectedParam.mfpMcId,
      sfpId: generateUUID(),
    };
    saveParameters([...parameters, newParam]);
    handleCloseModal();
  };


  const handleEditParameter = (parameter) => {
    setCurrentParameter(parameter);
    setParamValue(parameter.sfpValue);
    setEditMode(true);
  };

  const handleUpdateParameter = async () => {
    if (!currentParameter) {
      toast.warning('No parameter selected for update');
      return;
    }

    if (!hubParamsAllowEmptyValue(currentParameter.sfpName) && !paramValue.trim()) {
      toast.warning('Parameter value is required');
      return;
    }

    setLoading(true);
    try {
      const updatedParam = {
        sfpId: currentParameter.sfpId,
        sfpName: currentParameter.sfpName,
        sfpValue: paramValue,
        sfpMfId: currentParameter.sfpMfId,
        sfpInstanceId: flowInstanceId || null, // Preserve or set instance ID for Hub Flows
        sfpMcId: currentParameter.sfpMcId,
      };
      const response = await fetch(`${API_BASE_URL}/${currentParameter.sfpId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updatedParam),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const updatedData = await response.json();
      setParameters(parameters.map((param) =>
        param.sfpId === currentParameter.sfpId ? updatedData : param
      ));
      toast.success('Parameter updated successfully');
      setEditMode(false);
      setCurrentParameter(null);
      setParamValue('');
    } catch (error) {
      console.error('Error updating parameter:', error);
      toast.error(`Failed to update parameter: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteParameter = async (parameterId) => {
    if (!window.confirm('Are you sure you want to delete this parameter?')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/${parameterId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      setParameters(parameters.filter((param) => param.sfpId !== parameterId));
      toast.success('Parameter deleted successfully');
      if (currentParameter && currentParameter.sfpId === parameterId) {
        setEditMode(false);
        setCurrentParameter(null);
        setParamValue('');
      }
    } catch (error) {
      console.error('Error deleting parameter:', error);
      toast.error(`Failed to delete parameter: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveParameters = async (paramsToSave) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(paramsToSave),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const savedParams = await response.json();
      setParameters(savedParams);
      toast.success(editMode ? 'Parameter updated successfully' : 'Parameter added successfully');
    } catch (error) {
      console.error('Error saving parameters:', error);
      toast.error(`Failed to save parameters: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setCurrentParameter(null);
    setParamValue('');
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleRunStatusRowClick = async (record) => {
    if (!record?.srsId) return;
    setLoadingDetails(true);
    try {
      const response = await fetch(`${RUN_STATUS_API_URL}/${record.srsId}/details`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setSelectedRunDetails(data);
      setDetailsPopupOpen(true);
    } catch (error) {
      console.error('Error fetching hub run details:', error);
      toast.error(`Failed to load run details: ${error.message}`);
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 600 } }}>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
            Hub Flow Properties
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Typography variant="subtitle1" color="textSecondary" mb={2}>
          {masterFlowName || 'Hub Flow'}
        </Typography>
        <Divider sx={{ my: 2 }} />

        {/* Parameters section — Master Flow UI config */}
        {shouldShowSection('parameters') && (
        <Accordion
          expanded={expandedAccordion === 'parameters'}
          onChange={handleAccordionChange('parameters')}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Parameters</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {editMode ? (
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Edit Parameter: {currentParameter?.sfpName}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label="Parameter Value"
                  value={paramValue}
                  onChange={(e) => setParamValue(e.target.value)}
                  placeholder="Enter parameter value"
                  disabled={loading}
                  sx={{ mt: 1, mb: 1 }}
                />
                <Box display="flex" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateParameter}
                    disabled={
                      loading ||
                      (!hubParamsAllowEmptyValue(currentParameter?.sfpName || '') && !paramValue.trim())
                    }
                    sx={{ mr: 1 }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Update'}
                  </Button>
                  <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box mb={3}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={handleOpenAddModal}
                  disabled={loading}
                >
                  Add Parameter
                </Button>
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Parameter List
            </Typography>
            {loading && !filteredParameters.length ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : filteredParameters.length === 0 ? (
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  No parameters found for this flow
                </Typography>
              </Paper>
            ) : (
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {filteredParameters.map((param) => (
                  <React.Fragment key={param.sfpId}>
                    <ListItem
                      secondaryAction={
                        <Box>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleEditParameter(param)}
                            title="Edit parameter"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleDeleteParameter(param.sfpId)}
                            title="Delete parameter"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={param.sfpName}
                        secondary={param.sfpValue || 'N/A'}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>
        )}

        {/* Containers section */}
        {shouldShowSection('containers') && (
        <Accordion
          expanded={expandedAccordion === 'containers'}
          onChange={handleAccordionChange('containers')}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Containers</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" gutterBottom>
              Available Containers
            </Typography>
            {loadingContainers ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : containers.length === 0 ? (
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  No containers available
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                <Grid container spacing={2}>
                  {containers.map((container) => (
                    <Grid item xs={12} key={container.conId}>
                      <Box display="flex" alignItems="center">
                        <Checkbox
                          checked={selectedContainers.includes(container.conId)}
                          onChange={() => handleContainerToggle(container.conId)}
                          disabled={loading}
                        />
                        <Typography>{container.conDescription}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveContainers}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Save Containers'}
                  </Button>
                </Box>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        )}

        {/* Assigned Connectors section */}
        {shouldShowSection('assignedConnectors') && (
        <Accordion
          expanded={expandedAccordion === 'assignedConnectors'}
          onChange={handleAccordionChange('assignedConnectors')}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Assigned Connectors</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {loadingAssignedConnectors ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Assign Connectors for this Flow:
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {Array.isArray(masterConnectors) && masterConnectors.map((connector) => (
                      <Button
                        key={connector.mcId}
                        variant="outlined"
                        color="primary"
                        startIcon={<Add />}
                        onClick={() => handleOpenConnectorModal(connector.mcName)}
                        size="small"
                      >
                        {connector.mcName}
                      </Button>
                    ))}
                  </Box>
                </Box>

                {assignedConnectorsData.length === 0 ? (
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography color="textSecondary">
                      No connectors assigned to this flow
                    </Typography>
                  </Paper>
                ) : (
                  <Box>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Connector Type</strong></TableCell>
                            <TableCell><strong>Connector Description</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {assignedConnectorsData.map((assignment) => (
                            <TableRow key={assignment.sfcaId}>
                              <TableCell>{assignment.connectorType}</TableCell>
                              <TableCell>{assignment.connectorDescription || 'N/A'}</TableCell>
                              <TableCell>{assignment.connectorStatus}</TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenConnectorModal(assignment.connectorType)}
                                  title="Change Connector"
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveConnectorAssignment(assignment.connectorType)}
                                  title="Remove Assignment"
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>
        )}

        {/* Sync Run Status section */}
        {shouldShowSection('syncRunStatus') && (
        <Accordion
          expanded={expandedAccordion === 'runStatus'}
          onChange={handleAccordionChange('runStatus')}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Sync Run Status</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {loading && !runStatusRecords.length ? (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            ) : runStatusRecords.length === 0 ? (
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  No run status records found for this flow
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                <TableContainer component={Paper}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Instance ID</TableCell>
                        <TableCell>Start DateTime</TableCell>
                        <TableCell>End DateTime</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Remarks</TableCell>
                        <TableCell>Run Type</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRunStatusRecords.map((record) => (
                        <TableRow
                          key={record.srsId}
                          onClick={() => handleRunStatusRowClick(record)}
                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                        >
                          <TableCell>{record.srsInstanceId ?? record.srsFlowId ?? '—'}</TableCell>
                          <TableCell>
                            {new Date(record.srsStartDateTime).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {record.srsEndDateTime
                              ? new Date(record.srsEndDateTime).toLocaleString()
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={(() => {
                                const s = (record.srsStatus || '').trim();
                                const sl = s.toLowerCase();
                                if (s === 'COMPLETED' || sl === 'success') return 'SUCCESS';
                                return record.srsStatus;
                              })()}
                              size="small"
                              color={(() => {
                                const s = (record.srsStatus || '').trim();
                                const sl = s.toLowerCase();
                                if (s === 'SUCCESS' || s === 'COMPLETED' || sl === 'success') return 'success';
                                if (sl === 'failed' || sl === 'error' || s === 'ERROR' || s === 'FAILED') return 'error';
                                if (s === 'RUNNING' || sl === 'running') return 'info';
                                return 'default';
                              })()}
                            />
                          </TableCell>
                          <TableCell>{record.srsRemarks}</TableCell>
                          <TableCell>{record.srsRunType}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        )}

        {/* DataHub API Log section - Only for Hub Flows */}
        {isHubFlow && shouldShowSection('dataHubApiLog') && (
          <Accordion
            expanded={expandedAccordion === 'apiLogs'}
            onChange={handleAccordionChange('apiLogs')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>DataHub API Log</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {loading && !apiLogRecords.length ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : apiLogRecords.length === 0 ? (
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    No API log records found for this flow
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ maxHeight: 400, overflow: 'auto', width: '100%' }}>
                  <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table stickyHeader sx={{ minWidth: 1200 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>API Name</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Start Time</TableCell>
                          <TableCell>End Time</TableCell>
                          <TableCell>Duration (s)</TableCell>
                          <TableCell>Records Processed</TableCell>
                          <TableCell>Records Successful</TableCell>
                          <TableCell>Records Failed</TableCell>
                          <TableCell sx={{ minWidth: 300, maxWidth: 500 }}>Error Message</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {apiLogRecords.map((record) => (
                          <TableRow key={record.logId}>
                            <TableCell>{record.apiName}</TableCell>
                            <TableCell>
                              <Chip
                                label={record.status}
                                size="small"
                                color={
                                  record.status === 'SUCCESS' ? 'success' :
                                    record.status === 'FAILED' ? 'error' :
                                      record.status === 'WARNING' ? 'warning' :
                                        record.status === 'IN_PROGRESS' ? 'info' : 'default'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {record.startTime
                                ? new Date(record.startTime).toLocaleString()
                                : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {record.endTime
                                ? new Date(record.endTime).toLocaleString()
                                : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {record.durationSeconds
                                ? parseFloat(record.durationSeconds).toFixed(3)
                                : 'N/A'}
                            </TableCell>
                            <TableCell>{record.recordsProcessed || 0}</TableCell>
                            <TableCell>{record.recordsSuccessful || 0}</TableCell>
                            <TableCell>{record.recordsFailed || 0}</TableCell>
                            <TableCell sx={{
                              maxWidth: 500,
                              minWidth: 300,
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                            }}>
                              {(record.errorMessage || record.failureReason) ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: record.status === 'WARNING' ? 'warning.main' : 'error.main',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                  }}
                                  title={record.errorMessage || record.failureReason || ''}
                                >
                                  {record.errorMessage || record.failureReason || 'N/A'}
                                </Typography>
                              ) : (
                                'N/A'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </Box>

      {/* Add Parameter Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Add Parameter</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="parameter-select-label">Parameter</InputLabel>
            <Select
              labelId="parameter-select-label"
              value={selectedParameter}
              onChange={handleParameterSelect}
              label="Parameter"
              disabled={loadingParameters}
            >
              {loadingParameters ? (
                <MenuItem value="">Loading parameters...</MenuItem>
              ) : filteredAvailableParameters.length === 0 ? (
                <MenuItem value="">No parameters available</MenuItem>
              ) : (
                filteredAvailableParameters.map((param) => (
                  <MenuItem key={param.mfpId} value={param.mfpId}>
                    {param.mfpName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Parameter Value"
            value={paramValue}
            onChange={(e) => setParamValue(e.target.value)}
            placeholder="Enter parameter value"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            onClick={handleAddParameter}
            variant="contained"
            disabled={
              !selectedParameter ||
              (paramValue.trim() === '' &&
                !hubParamsAllowEmptyValue(
                  filteredAvailableParameters.find((p) => p.mfpId === selectedParameter)?.mfpName || ''
                ))
            }
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Connector Modal */}
      <Dialog open={connectorModalOpen} onClose={() => {
        setConnectorModalOpen(false);
        setSelectedConnectorId('');
        setSelectedConnectorType('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Assign {selectedConnectorType} Connector</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="connector-select-label">Available Connectors</InputLabel>
            <Select
              labelId="connector-select-label"
              value={selectedConnectorId || ''}
              onChange={(e) => setSelectedConnectorId(e.target.value)}
              label="Available Connectors"
              disabled={loadingAssignedConnectors}
              error={!selectedConnectorId && availableConnectorsForAssignment.length > 0}
            >
              {loadingAssignedConnectors ? (
                <MenuItem value="">Loading connectors...</MenuItem>
              ) : availableConnectorsForAssignment.length === 0 ? (
                <MenuItem value="">No connectors available</MenuItem>
              ) : (
                availableConnectorsForAssignment.map((connector) => {
                  // Handle both response formats: {id, description, status} or {ctdId, ctdDescription}
                  const connectorId = connector.id || connector.ctdId;
                  const connectorDescription = connector.description || connector.ctdDescription;
                  const connectorStatus = connector.status;

                  return (
                    <MenuItem key={connectorId} value={connectorId}>
                      {connectorDescription || `Connector ${connectorId?.substring(0, 8)}...`}
                      {connectorStatus && ` - ${connectorStatus}`}
                    </MenuItem>
                  );
                })
              )}
            </Select>
            {!selectedConnectorId && availableConnectorsForAssignment.length > 0 && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                Please select a connector
              </Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setConnectorModalOpen(false);
            setSelectedConnectorId('');
            setSelectedConnectorType('');
          }}>Cancel</Button>
          <Button
            onClick={handleAssignConnector}
            variant="contained"
            disabled={!selectedConnectorId || loadingAssignedConnectors}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Container Modal */}
      <Dialog open={assignContainerModalOpen} onClose={handleCloseAssignContainerModal} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Container</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Container</InputLabel>
            <Select
              value={selectedContainer}
              onChange={(e) => setSelectedContainer(e.target.value)}
              disabled={loadingContainers}
            >
              {containers.map((container) => (
                <MenuItem key={container.conId} value={container.conId}>
                  {container.conDescription || `Container ${container.conId?.substring(0, 8)}...`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignContainerModal}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!selectedContainer || !masterFlowId) {
                toast.warning('Please select a container');
                return;
              }
              setLoading(true);
              try {
                const assignmentData = {
                  conId: selectedContainer,
                  sflId: masterFlowId,
                };
                const response = await fetch(CONTAINER_ASSIGNMENTS_API_URL, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify(assignmentData),
                });
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                toast.success('Container assigned successfully');
                handleCloseAssignContainerModal();
                fetchAssignedContainers();
              } catch (error) {
                console.error('Error assigning container:', error);
                toast.error(`Failed to assign container: ${error.message}`);
              } finally {
                setLoading(false);
              }
            }}
            variant="contained"
            disabled={!selectedContainer || loading}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailsPopupOpen}
        onClose={() => setDetailsPopupOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Hub Run Details
          <IconButton
            onClick={() => setDetailsPopupOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : selectedRunDetails ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Hub Tracks
                  </Typography>
                  {!(selectedRunDetails.hubTracks || []).length ? (
                    <Typography variant="body2" color="text.secondary">
                      No hub track rows for this run yet. Hub orchestration must call HubTrackService
                      per transferred record.
                    </Typography>
                  ) : (
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Source ID</TableCell>
                            <TableCell>Target ID</TableCell>
                            <TableCell>Document Type</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Message</TableCell>
                            <TableCell>Time</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(selectedRunDetails.hubTracks || []).map((track) => (
                            <TableRow key={track.id}>
                              <TableCell>{track.sourceId || '—'}</TableCell>
                              <TableCell>{track.targetId || '—'}</TableCell>
                              <TableCell>{track.documentType || '—'}</TableCell>
                              <TableCell>{track.status || 'N/A'}</TableCell>
                              <TableCell>{track.message || 'N/A'}</TableCell>
                              <TableCell>
                                {track.syncDateTime ? formatAppDateTime(track.syncDateTime) : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Grid>

              {(selectedRunDetails.hubErrorRecords || []).length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Hub Error Records
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Source ID</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Message</TableCell>
                            <TableCell>Time</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(selectedRunDetails.hubErrorRecords || []).map((error) => (
                            <TableRow key={error.id}>
                              <TableCell>{error.sourceId || '—'}</TableCell>
                              <TableCell>{error.errorType || '—'}</TableCell>
                              <TableCell>{error.message || '—'}</TableCell>
                              <TableCell>
                                {error.syncDateTime ? formatAppDateTime(error.syncDateTime) : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography color="textSecondary">No details available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Drawer>
  );
};

export default HubFlowParameter;

